<template>
  <CModal
    class="phone-dial-modal"
    title="☏ Phone Dial"
    color="white"
    :show.sync="show"
    centered
    :closeOnBackdrop="false"
    size="sm"
  >
    <!-- Status Card -->
    <div class="status-card mb-3" :class="statusCardClass">
      <div class="status-icon">
        <CIcon :name="statusIcon" size="lg" />
      </div>
      <div class="status-content">
        <h6 class="mb-1">{{ statusTitle }}</h6>
        <small>{{ statusMessage }}</small>
      </div>
    </div>

    <!-- Phone Input -->
    <CForm @submit.prevent="dial">
      <div class="input-card">
        <div class="input-header">
          <CIcon name="cil-phone" class="mr-2" />
          <strong>Phone Number</strong>
        </div>
        <CInput
          v-model.trim="$v.form.phone.$model"
          placeholder="Enter phone number"
          class="phone-input"
          size="lg"
        />
      </div>
    </CForm>

    <template #footer>
      <div class="vertical-center flex-gap-2">
        <CButton @click="close" color="link">Cancel</CButton>
        <CButton @click="dial" color="success" class="dial-btn" :disabled="loading || $v.form.$invalid || serviceDisabled || !userExtension || userAccountDisabled">
          <CSpinner size="sm" v-if="loading" />
          <CIcon v-else name="cil-phone" />
          <span v-if="!loading">Dial</span>
          <span v-else>Dialing...</span>
        </CButton>
      </div>
    </template>
  </CModal>
</template>

<script>
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { helpers, required } from "vuelidate/lib/validators";

const phone = helpers.regex(
  "phone",
  /^\+?[0-9]{7,15}$/ // + ile başlayabilir, 7-15 arası rakam olmalı
)

export default {
  name: "PhoneDialModal",
  mixins: [validationMixin],
  data() {
    return {
      loading: false,
      form: {
        phone: null,
      },
    };
  },
  validations: {
    form: {
      phone: { phone, required },
    },
  },
  computed: {
    ...mapGetters(["errors", "voipEnabled", "voipAccountStatus"]),

    show: {
      get() { return this.$store.state.voip.phoneDialModal.show; },
      set(value) { if (!value) this.close(); }
    },

    formIsInvalid() { return this.$v.form.$invalid && this.form.phone !== null },

    // VoIP Service Status
    serviceDisabled: {
      get() { return this.voipEnabled === false; },
      set(value) { this.$store.commit('setVoipEnabled', !value); }
    },

    // VoIP Extension (Callee Extension)
    userExtension() { return this.authUser?.user?.voip_extension; },

    userAccountDisabled: {
      get() { return [null, 'Unavailable'].includes(this.voipAccountStatus) },
      set(value) { this.$store.commit('setVoipAccountStatus', value || null); }
    },

    // Called Phone Number
    phoneNumber() { return this.$store.state.voip.phoneDialModal.phoneNumber; },

    statusCardClass() {
      if (this.serviceDisabled || this.userAccountDisabled) return 'status-danger';
      if (!this.userExtension) return 'status-warning';
      if (this.formIsInvalid) return 'status-danger';
      return 'status-success';
    },
    statusIcon() {
      if (this.serviceDisabled || this.userAccountDisabled) return 'cil-ban';
      if (!this.userExtension) return 'cil-warning';
      if (this.formIsInvalid) return 'cil-ban';
      return 'cil-phone';
    },
    statusTitle() {
      if (this.serviceDisabled) return 'VoIP Service Disabled';
      if (!this.userExtension) return 'No VoIP Extension';
      if (this.userAccountDisabled) return 'VoIP Account Unavailable';
      if (this.formIsInvalid) return 'Invalid Phone Number';
      return 'Ready to Dial';
    },
    statusMessage() {
      if (this.serviceDisabled) return 'VoIP service is currently disabled by administrator.';
      if (!this.userExtension) return 'VoIP extension not assigned! Contact administrator.';
      if (this.userAccountDisabled) return `Your VoIP account related to extension ${this.userExtension} is unavailable!`;
      if (this.formIsInvalid) return 'Please enter a valid phone number.';
      return `Extension ${this.userExtension} - Ready to make call`;
    },
  },
  watch: {
    phoneNumber(newVal) {
      if (newVal) {
        this.form.phone = newVal.replace(/\s+/g, '');
      }
    },
    show(newVal) {
      if (!newVal) {
        this.reset();
      }
    }
  },
  methods: {
    async dial() {
      this.$v.$touch();

      if (this.$v.form.$invalid) return;

      // Check if VoIP service is disabled or user has no extension
      if (this.serviceDisabled || !this.userExtension) {
        return;
      }

      this.loading = true;
      try {
        const response = await this.$axios.post(this.$backend.VOIP.DIAL_OUTBOUND, {
          caller: this.userExtension,
          outbound: this.form.phone,
        });

        const { outbound } = response.data;
        this.$toast.success(`Dialing to ${outbound} was initiated successfully!`);
        this.close();
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "An unknown error occurred during dial.";
        this.$toast.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.$store.commit('closePhoneDialModal');
    },

    reset() {
      this.form.phone = null;
      this.$v.$reset();
    },
  },
};
</script>

<style>
.phone-dial-modal {
  --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --success-color: #10b981;
  --danger-color: #ef4444;
  --warning-color: #f59e0b;
  --light-bg: #f8fafc;
  --border-color: #e2e8f0;
  --shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  --border-radius: 12px;
}

.phone-dial-modal .modal-content {
  border-radius: var(--border-radius);
  background-clip: border-box;
}

.phone-dial-modal .modal-header {
  background: var(--primary-gradient) !important;
  color: white;
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  border: none;
  box-shadow: var(--shadow);
}

.phone-dial-modal .modal-header .close {
  color: white;
  font-size: 1.6rem;
  font-weight: normal;
  text-shadow: none;
}

.phone-dial-modal .modal-footer {
  padding: 0.25rem !important;
}

.input-card {
  background: var(--light-bg);
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
}

.input-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #374151;
  font-size: 0.875rem;
}

.phone-input {
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  letter-spacing: 2px;
  background: white;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}


/* Status Card */
.status-card {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.status-success {
  background: #f0f9ff;
  border-left-color: var(--success-color);
}

.status-danger {
  background: #fef2f2;
  border-left-color: var(--danger-color);
}

.status-warning {
  background: #fffbeb;
  border-left-color: var(--warning-color);
}

.status-icon {
  margin-right: 0.75rem;
}

.status-content {
  flex: 1;
}


/* Buttons */
.dial-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

@media (max-width: 768px) {
  .phone-input {
    font-size: 1.25rem;
    letter-spacing: 1px;
    padding: 0.5rem 0.75rem;
  }
}
</style>
